import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Table } from '@opendate-app/setlist';
import client from '../../../shared/api/client';
import ReportingTableToolbar from './ReportingTableToolbar';
import useTableColumns from '../hooks/useTableColumns';
import useSortRules from '../hooks/useSortRules';
import { SORT_TYPES } from '../constants/sortTypes';
import ReportingPageLayout from './ReportingPageLayout';
import ReportingPledgesTable from './ReportingPledgesTable';
import TeamPagination from '../../TeamManager/components/TeamPagination';
import useReportingPage from '../hooks/useReportingPage';
import { currencyFormatter } from '../utils/reportingUtils';

const COLUMNS = [
  { id: 'donor_name', label: 'Donor', sortable: true, sortType: 'text', alwaysVisible: true },
  { id: 'email', label: 'Email', sortable: true, sortType: 'text' },
  { id: 'amount', label: 'Amount', sortable: true, sortType: 'currency' },
  { id: 'date', label: 'Date', sortable: true, sortType: 'date' },
];

function ReportingGivingDonationsPage({
  team, title, subtitle, sidebarKey, backHref, backLabel,
  giftsEndpoint, exportEndpoint, pledgesEndpoint, drilldownId, startDate, endDate, reportingTimeZone,
}) {
  const {
    isMobile, sidebarOpen, setSidebarOpen,
    dateRange, setDateRange,
    filterSidebarOpen, setFilterSidebarOpen,
    activeFilters,
    handleFilterChange, handleRemoveFilter, handleClearAllFilters,
  } = useReportingPage();

  const [tableSettingsOpen, setTableSettingsOpen] = useState(false);
  const { visibleColumns, hiddenColumns, handleColumnsChange } = useTableColumns({
    columns: COLUMNS,
    localStorageKey: `reportingGivingDonationsColumns_${team && team.id}_${sidebarKey}`,
    teamId: team && team.id,
  });
  const {
    sortRules, sortPopoverOpen, setSortPopoverOpen, sortableColumns,
    handleColumnSort, handleSortRuleChange, handleAddSortRule, handleRemoveSortRule, sortArrow,
  } = useSortRules({ columns: COLUMNS, defaultSort: { field: 'date', direction: 'desc' } });

  const queryKey = JSON.stringify([team && team.id, giftsEndpoint, drilldownId, startDate, endDate, sortRules]);
  const [pageState, setPageState] = useState({ queryKey, page: 1 });
  const page = pageState.queryKey === queryKey ? pageState.page : 1;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [donations, setDonations] = useState([]);
  const [pagination, setPagination] = useState({ current_page: 1, total_pages: 1, total_entries: 0, per_page: 25 });

  useEffect(() => {
    setPageState((current) => current.queryKey === queryKey ? current : { queryKey, page: 1 });
  }, [queryKey]);

  useEffect(() => {
    if (!team || !team.id) return undefined;
    let active = true;
    setLoading(true);
    setError(null);

    const params = {
      drilldown_id: drilldownId, page, per_page: 25,
      sort_by: sortRules.map((rule) => rule.field),
      sort_direction: sortRules.map((rule) => rule.direction),
    };
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    client.get(giftsEndpoint, { params })
      .then((res) => {
        if (!active) return;
        setDonations(res.data.donations || []);
        setPagination(res.data.pagination || { current_page: 1, total_pages: 1, total_entries: 0, per_page: 25 });
      })
      .catch(() => { if (active) setError('Failed to load donations.'); })
      .finally(() => { if (active) setLoading(false); });

    return () => { active = false; };
  }, [team && team.id, giftsEndpoint, drilldownId, startDate, endDate, page, sortRules]);

  const formatDate = (value) => {
    if (!value) return '—';
    return new Date(value).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      ...(reportingTimeZone ? { timeZone: reportingTimeZone } : {}),
    });
  };

  const total = pagination.total_entries || 0;
  const canCrm = team && team.package && team.package.crm;

  const exportParams = new URLSearchParams({ drilldown_id: drilldownId });
  if (startDate) exportParams.set('start_date', startDate);
  if (endDate) exportParams.set('end_date', endDate);
  sortRules.forEach((rule) => {
    exportParams.append('sort_by[]', rule.field);
    exportParams.append('sort_direction[]', rule.direction);
  });

  const renderCell = (column, row) => {
    switch (column.id) {
      case 'donor_name':
        return canCrm && row.fan_id ? (
          <a href={`/teams/${team.id}/crm/fans/${row.fan_id}`}>{row.donor_name || '(No name)'}</a>
        ) : (row.donor_name || '(No name)');
      case 'email': return row.email || '—';
      case 'amount': return currencyFormatter.format(row.amount || 0);
      case 'date': return formatDate(row.date);
      default: return '—';
    }
  };

  return (
    <ReportingPageLayout
      team={team}
      activeKey={sidebarKey}
      title={title}
      subtitle={subtitle}
      dateRange={dateRange}
      setDateRange={setDateRange}
      isMobile={isMobile}
      sidebarOpen={sidebarOpen}
      setSidebarOpen={setSidebarOpen}
      filterSidebarOpen={filterSidebarOpen}
      setFilterSidebarOpen={setFilterSidebarOpen}
      activeFilters={activeFilters}
      filterGroups={[]}
      handleFilterChange={handleFilterChange}
      handleRemoveFilter={handleRemoveFilter}
      handleClearAllFilters={handleClearAllFilters}
      showDatePicker={false}
      showFilters={false}
    >
      <a href={backHref} className="d-inline-flex align-items-center mb-3" style={{ fontSize: 13, fontWeight: 500, color: 'var(--sl-fg-brand, #1982c4)', textDecoration: 'none' }}>
        <i className="fas fa-arrow-left mr-2" style={{ fontSize: 12 }} />
        Back to {backLabel}
      </a>

      {error && <div className="alert alert-danger">{error}</div>}

      <div style={{ background: 'var(--sl-bg-primary, #fff)', border: '1px solid var(--sl-border-secondary, #e4e7ec)', borderRadius: 8 }}>
        <ReportingTableToolbar
          title="Donations"
          exportUrl={exportEndpoint ? `${exportEndpoint}?${exportParams.toString()}` : undefined}
          tableSettingsOpen={tableSettingsOpen}
          setTableSettingsOpen={setTableSettingsOpen}
          visibleColumns={visibleColumns}
          hiddenColumns={hiddenColumns}
          onColumnsChange={handleColumnsChange}
          sortRules={sortRules}
          sortPopoverOpen={sortPopoverOpen}
          setSortPopoverOpen={setSortPopoverOpen}
          sortableColumns={sortableColumns}
          sortTypes={SORT_TYPES}
          onSortRuleChange={handleSortRuleChange}
          onAddSortRule={handleAddSortRule}
          onRemoveSortRule={handleRemoveSortRule}
          isMobile={isMobile}
        />
        <div className="px-4 py-2 text-muted" aria-live="polite">
          {loading ? 'Loading…' : `${total.toLocaleString()} donation${total !== 1 ? 's' : ''}`}
        </div>
        <Table
          columns={visibleColumns.map((column) => ({
            key: column.id,
            header: <>{column.label}{sortRules.length > 1 ? sortArrow(column.id) : ''}</>,
            sortable: true,
            primary: column.id === 'donor_name',
            render: (row) => renderCell(column, row),
          }))}
          data={loading || error ? [] : donations}
          getRowKey={(row) => row.id}
          sort={sortRules.length === 1 ? { key: sortRules[0].field, direction: sortRules[0].direction } : undefined}
          onSortChange={handleColumnSort}
          empty={loading ? 'Loading…' : error ? 'Donations could not be loaded.' : 'No donations in this period.'}
        />

        {pagination.total_pages > 1 && (
          <div style={{ padding: '12px 24px', borderTop: '1px solid var(--sl-bg-tertiary, #f2f4f7)' }}>
            <TeamPagination
              currentPage={pagination.current_page}
              totalPages={pagination.total_pages}
              totalItems={total}
              pageSize={pagination.per_page || 25}
              itemLabel="donations"
              onPageChange={(p) => { if (!loading && p >= 1 && p <= pagination.total_pages) setPageState({ queryKey, page: p }); }}
            />
          </div>
        )}
      </div>

      {pledgesEndpoint && (
        <ReportingPledgesTable
          team={team}
          endpoint={pledgesEndpoint}
          drilldownId={drilldownId}
          startDate={startDate}
          endDate={endDate}
        />
      )}
    </ReportingPageLayout>
  );
}

ReportingGivingDonationsPage.propTypes = {
  team: PropTypes.object.isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  sidebarKey: PropTypes.string.isRequired,
  backHref: PropTypes.string.isRequired,
  backLabel: PropTypes.string.isRequired,
  giftsEndpoint: PropTypes.string.isRequired,
  exportEndpoint: PropTypes.string,
  pledgesEndpoint: PropTypes.string,
  reportingTimeZone: PropTypes.string,
  drilldownId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
};

export default ReportingGivingDonationsPage;

